import { InMemoryCache } from '@apollo/client';
import Cookies from 'js-cookie';
import { getBomCountryCookie } from 'shop/lib/util/client.util/countryCookie';
import { CURRENT_USER, GET_USER_LOCALE } from '../components/App.gql';
import { updateCartCountry } from '../services/storefront/cart/index';
import { isBrowser } from '../util/ssr';
import { deepEqual, getCookieDomain, replaceAllSpaces } from '../util/util';

/**
 * Creates an instance of InMemoryCache with preloaded values and custom type policies.
 */
export const createApolloCache = (localState) => {
  const cache = new InMemoryCache({
    typePolicies: {
      /**
       * Parses out the admin id from the namespaced gid which isn't consistent
       * https://community.shopify.com/c/shopify-apis-and-sdks/storefront-customeraddressupdate-modifies-address-id/m-p/1463067
       */
      MailingAddress: {
        fields: {
          adminId: {
            read(_, { readField }) {
              const gid = readField('id');
              const adminId = gid.substring(
                gid.lastIndexOf('/') + 1,
                gid.indexOf('?')
              );
              return adminId;
            }
          }
        },
        keyFields: ['adminId']
      },
      ProductVariant: {
        fields: {
          color: {
            read(_, { readField }) {
              const selectedOptions = readField('selectedOptions');
              const colorOption = selectedOptions.find(
                (option) => option.name.toLowerCase() === 'color'
              )?.value;

              return colorOption ? replaceAllSpaces(colorOption) : null;
            }
          }
        }
      },
      SizeReference: {
        keyFields: false
      },
      BaseCartLineConnection: {
        fields: {
          productVariantCount: {
            read(_, { readField }) {
              const edges = readField('edges') || [];

              const count = edges.reduce(
                (sum, lineItem) => sum + lineItem.node.quantity,
                0
              );

              return count;
            }
          }
        }
      },
      Settings: {
        fields: {
          values: {
            read(_, { readField }) {
              const resources = readField('resources');
              return resources.reduce((acc, curr) => {
                const { key, ...values } = curr;
                return { ...acc, [key]: values };
              }, {});
            }
          }
        }
      },
      AccountPreference: {
        keyFields: ['preferenceType']
      },
      User: {
        keyFields: ['email']
      },
      SizeGroup: {
        keyFields: false
      },
      Customer: {
        fields: {
          orders: {
            keyArgs: false,
            merge(existing = { edges: [] }, incoming, { readField }) {
              return {
                ...incoming,
                edges: [
                  ...readField('edges', existing),
                  ...readField('edges', incoming)
                ]
              };
            }
          }
        }
      },
      Variant: {
        fields: {
          price: {
            merge: true
          },
          externalSystems: {
            merge: true
          }
        }
      },
      Color: {
        keyFields: ['id', 'swatch', ['id']]
      },
      Asset: {
        fields: {
          file: {
            merge: true
          }
        }
      },
      Product: {
        fields: {
          externalSystems: {
            merge: true
          }
        }
      },
      ShopifyLineItem: {
        fields: {
          discountAllocations: {
            merge: true
          }
        }
      },
      ShopifyFulfillments: {
        fields: {
          trackingInfo: {
            merge: true
          }
        }
      },
      Filter: {
        /**
         * These keys are dependent on Filter Options have unique ids based on isDisabled
         * @see {@link FilterOption}
         */
        keyFields: ['id', 'options']
      },
      FilterOption: {
        /**
         * Since isDisabled is a dynamic boolean value, we need to ensure that the cache is
         * updated when the value changes, otherwise the filterOption will use the wrong cached data.
         */
        keyFields: ['id', 'isDisabled']
      },
      ProductGrid: {
        fields: {
          items: {
            keyArgs: ['options', ['sort', 'filters']],
            merge(existing = [], incomingRaw, { args: { options } }) {
              const merged = Array.isArray(existing) ? existing.slice(0) : [];

              const incoming = incomingRaw?.filter(
                (item) => item.__typename !== 'Filters'
              );

              if (Array.isArray(incoming)) {
                if (options) {
                  const { offset = 0 } = options;

                  if (deepEqual(merged, incoming)) return merged;

                  /**
                   * We cannot rely on the number of products being returned in each fetch,
                   * since some products are split into multiple product cards,
                   * e.g. split by Featured Colors.
                   *
                   * To ensure the items are in order with an unknown items.length
                   * in each fetched "page", we store each page into its own index
                   * which created a nested array.
                   *
                   * Then, in the Product Grid, we can flatten the resulting array
                   * with Array.flat()
                   *
                   * @see ProductGrid
                   */
                  merged[offset] = incoming;
                } else {
                  merged[0] = incoming;
                }
              }

              return merged;
            }
          }
        }
      },
      Query: {
        fields: {
          cartId: {
            read() {
              return Cookies.get('BOM-CART_ID') || false;
            }
          },
          customerAccessToken: {
            read() {
              return Cookies.get('BOM-CAT') || '';
            }
          },
          user: {
            merge: true
          },
          localization: {
            merge: true
          },
          referenceAttributes: {
            merge: true
          }
        }
      }
    }
  });

  const localeData = isBrowser()
    ? getBomCountryCookie()
    : localState?.locale || {};

  cache.writeQuery({
    query: CURRENT_USER,
    data: {
      user: {
        ...JSON.parse(Cookies.get('BOM-USER') || '{}'),
        hasShopifySession: !!Cookies.get('BOM-SESSION_INIT'),
        locale: {
          country: localeData.country || '',
          isConfirmed: localeData.isConfirmed || false,
          __typename: 'Locale'
        }
      }
    }
  });

  cache.watch({
    query: GET_USER_LOCALE,
    callback: ({ result }) => {
      const userLocale = result.user.locale;

      if (isBrowser() && userLocale) {
        Cookies.set(
          'BOM-COUNTRY',
          `${userLocale.country}:${userLocale.isConfirmed ? 1 : 0}`,
          {
            domain: getCookieDomain(),
            expires: 365
          }
        );

        // Update Shopify Cart Country
        updateCartCountry(userLocale.country);
      }
    }
  });

  return cache;
};

export default {};
